import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ArrowLeftIcon, ZapIcon, ArrowDownIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { trackWizardStep, trackWizardComplete } from '../../utils/metaPixel';
import { getTrafficIntent } from '../../utils/trafficIntent';
import { loadAnswers, saveAnswers, loadStep, saveStep, saveCompleted, clearWizardStorage, EMPTY_ANSWERS, type WizardAnswers } from '../../utils/wizardStorage';
type Option = {
  value: string;
  label: string;
};
type StepId = 'procedure' | 'timeline' | 'credit' | 'medical' | 'body';
const STEP_ORDER: StepId[] = ['procedure', 'timeline', 'credit', 'medical', 'body'];
const HIGH_INTENT_PROCEDURE = 'Lipolaser / Lipo de Papada';
const easeOutCubic = [0.33, 1, 0.68, 1];
type QualifyWizardProps = {
  onComplete: (answers: WizardAnswers) => void;
};
export function QualifyWizard({
  onComplete
}: QualifyWizardProps) {
  const {
    t
  } = useLanguage();
  const [intent] = useState(() => getTrafficIntent());
  const [answers, setAnswers] = useState<WizardAnswers>(EMPTY_ANSWERS);
  const [step, setStep] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  const headingRef = useRef<HTMLParagraphElement | null>(null);
  const isFirstRenderRef = useRef(true);

  // Restore progress + pre-select the advertised procedure for HI traffic
  useEffect(() => {
    const stored = loadAnswers();
    const restored: WizardAnswers = {
      ...stored,
      procedure: stored.procedure || (intent.isHighIntent ? HIGH_INTENT_PROCEDURE : null)
    };
    setAnswers(restored);
    setStep(Math.min(loadStep(), STEP_ORDER.length - 1));
    setHydrated(true);
  }, [intent.isHighIntent]);
  useEffect(() => {
    if (!hydrated) return;
    saveAnswers(answers);
  }, [answers, hydrated]);
  useEffect(() => {
    if (!hydrated) return;
    saveStep(step);
    // Never steal focus on first paint — that would yank the page down to the
    // wizard before the patient has read anything.
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    headingRef.current?.focus({
      preventScroll: true
    });
  }, [step, hydrated]);
  const stepId = STEP_ORDER[step];
  const total = STEP_ORDER.length;
  const stepCopy = t(`wizard.steps.${stepId}`);
  const goNext = (answerLabel?: string) => {
    trackWizardStep(step, stepId, answerLabel);
    if (step < total - 1) {
      setStep(step + 1);
      return;
    }
    trackWizardComplete({
      procedure: answers.procedure ?? undefined,
      timeline: answers.timeline ?? undefined,
      credit: answers.credit ?? undefined,
      medical: answers.medical.join(', ') || undefined
    });
    saveCompleted();
    onComplete(answers);
  };
  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };
  const selectSingle = (field: 'procedure' | 'timeline' | 'credit') => (option: Option) => {
    setAnswers(prev => ({
      ...prev,
      [field]: option.value
    }));
    // Brief pause so the patient sees their tap register before we advance
    window.setTimeout(() => goNext(option.value), 220);
  };
  const toggleMedical = (option: Option) => {
    setAnswers(prev => {
      if (option.value === 'None') {
        return {
          ...prev,
          medical: prev.medical.includes('None') ? [] : ['None']
        };
      }
      const withoutNone = prev.medical.filter(v => v !== 'None');
      return {
        ...prev,
        medical: withoutNone.includes(option.value) ? withoutNone.filter(v => v !== option.value) : [...withoutNone, option.value]
      };
    });
  };
  const canAdvance = (() => {
    if (stepId === 'procedure') return !!answers.procedure;
    if (stepId === 'timeline') return !!answers.timeline;
    if (stepId === 'credit') return !!answers.credit;
    if (stepId === 'medical') return answers.medical.length > 0;
    return !!answers.height && !!answers.weight;
  })();
  const selectedValue = stepId === 'procedure' ? answers.procedure : stepId === 'timeline' ? answers.timeline : stepId === 'credit' ? answers.credit : null;
  const progressPct = (step + 1) / total * 100;
  const isMulti = stepId === 'medical';
  const helperKey = stepId === 'body' ? 'body' : isMulti ? 'multi' : 'single';
  return <div className="rounded-[2rem] border border-brand-gold/20 bg-brand-cream p-6 sm:p-8 lg:p-10 shadow-2xl shadow-black/15" data-id="element-373" data-mp="3qlsdpalshi8r">
      {/* Header */}
      <div className="mb-6" data-id="element-374" data-mp="4d9b6rbxj1ul">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5" data-id="element-375" data-mp="4by6mpantq2f">
          <div className="inline-flex items-center gap-3" data-id="element-376" data-mp="947v00w4mi2l">
            <span className="w-6 h-px bg-brand-gold" data-id="element-377" data-mp="95snzm2slsyc"></span>
            <span className="text-[10px] font-medium tracking-[0.25em] uppercase text-brand-dark/50" data-id="element-378" data-mp="37soe78lw4b68">
              {t('wizard.eyebrow')}
            </span>
          </div>
          {intent.isHighIntent && <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold/15 px-3 py-1.5 text-[10px] font-medium tracking-widest uppercase text-brand-dark/70" data-id="element-379" data-mp="357ysbk7mlwbn">
              <ZapIcon className="h-3 w-3 text-brand-gold" strokeWidth={2} data-id="element-380" data-mp="2vaayqxlrauv2" />
              {t('wizard.highIntentBadge')}
            </span>}
        </div>

        <h3 className="font-serif text-2xl sm:text-3xl font-light text-brand-dark leading-tight mb-2" data-id="element-381" data-mp="2mafiiedcg9o6">
          {t('wizard.heading')}
        </h3>
        <p className="text-sm text-brand-dark/60 font-light leading-relaxed" data-id="element-382" data-mp="2hixhjqbbm937">
          {t('wizard.subhead')}
        </p>
      </div>

      {/* Two-phase roadmap — the patient always knows what is coming */}
      <div className="mb-6 flex items-center gap-3 rounded-2xl bg-brand-dark/[0.04] px-4 py-3" data-id="element-383" data-mp="27mujowni4wm2">
        <span className="inline-flex items-center gap-2" data-id="element-384" data-mp="22l56bact46ut">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-gold text-[10px] font-semibold text-white" data-id="element-385" data-mp="1yl3edswvirsq">
            1
          </span>
          <span className="text-[11px] font-medium text-brand-dark" data-id="element-386" data-mp="1o1u96gr7y2vv">
            {t('wizard.stepper.questions')}
          </span>
        </span>
        <span className="h-px flex-1 bg-brand-dark/15" data-id="element-387" data-mp="1ezl2y32zgvoa" />
        <span className="inline-flex items-center gap-2" data-id="element-388" data-mp="1f14j7ton36q2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-dark/25 text-[10px] font-semibold text-brand-dark/40" data-id="element-389" data-mp="1jz5ye3iy2lw9">
            2
          </span>
          <span className="text-[11px] font-medium text-brand-dark/45" data-id="element-390" data-mp="2bpk8rprr1g36">
            {t('wizard.stepper.details')}
          </span>
        </span>
      </div>

      {/* Progress */}
      <div className="mb-7" data-id="element-391" data-mp="22wqr5loty4m5">
        <div className="flex items-center justify-between mb-2.5" data-id="element-392" data-mp="22vyus8nes2ut">
          <span className="text-[10px] font-medium tracking-widest uppercase text-brand-dark/45" data-id="element-393" data-mp="2qmuqhguzji7c">
            {t('wizard.progress')} {step + 1} {t('wizard.of')} {total}
          </span>
          <span className="text-[10px] font-medium tracking-widest uppercase text-brand-gold" data-id="element-394" data-mp="34s3hxa4t7gal">
            {t('wizard.promise')}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-brand-dark/10" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={total} data-id="element-395" data-mp="30q02ybixltkk">
          <motion.div className="h-full rounded-full bg-brand-gold" initial={false} animate={{
          width: `${progressPct}%`
        }} transition={{
          duration: 0.5,
          ease: easeOutCubic
        }} data-id="element-396" data-mp="39l61qqjsbbdl" />
        </div>
      </div>

      {/* Step body — fixed min height so the card never jumps between steps */}
      <div className="min-h-[420px] sm:min-h-[400px]" data-id="element-397" data-mp="b17j2e2901jw">
        <AnimatePresence mode="wait" data-id="element-398" data-mp="azwj6pbxbdy2">
          <motion.div key={stepId} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3,
          ease: easeOutCubic
        }} data-id="element-399" data-mp="b30oxr51ehqu">
            <p ref={headingRef} tabIndex={-1} className="font-serif text-xl sm:text-2xl font-light text-brand-dark leading-snug mb-2 outline-none" data-id="element-400" data-mp="2e9gaonvgdmpr">
              {stepCopy.question}
            </p>
            <p className="text-xs text-brand-dark/55 font-light leading-relaxed mb-2" data-id="element-401" data-mp="2o6cmp3kk8qz2">
              {stepCopy.hint}
            </p>
            <p className="mb-5 text-[11px] font-medium text-brand-gold" data-id="element-402" data-mp="2x8lsjg4vvw64">
              {t(`wizard.helper.${helperKey}`)}
            </p>

            {stepId === 'body' ? <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-403" data-mp="xxb0r5mqkfdg">
                <RangeColumn label={stepCopy.heightLabel} options={stepCopy.heightOptions as Option[]} selected={answers.height} onSelect={value => setAnswers(prev => ({
              ...prev,
              height: value
            }))} data-id="element-404" data-mp="12vm5x32361pc" />
                <RangeColumn label={stepCopy.weightLabel} options={stepCopy.weightOptions as Option[]} selected={answers.weight} onSelect={value => setAnswers(prev => ({
              ...prev,
              weight: value
            }))} data-id="element-405" data-mp="gn71zz5ot0yq" />
              </div> : <div className="space-y-2.5" role={isMulti ? 'group' : 'radiogroup'} aria-label={stepCopy.question} data-id="element-406" data-mp="109vc3j23rgh9">
                {(stepCopy.options as Option[]).map(option => {
              const isSelected = isMulti ? answers.medical.includes(option.value) : selectedValue === option.value;
              return <button key={option.value} type="button" role={isMulti ? 'checkbox' : 'radio'} aria-checked={isSelected} onClick={() => isMulti ? toggleMedical(option) : selectSingle(stepId as 'procedure' | 'timeline' | 'credit')(option)} className={`group flex w-full items-center justify-between gap-4 rounded-2xl border px-5 py-4 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold ${isSelected ? 'border-brand-gold bg-brand-gold/10' : 'border-brand-dark/12 bg-white hover:border-brand-gold/50'}`} data-id="element-407" data-mp="39kml0ejajq3q">
                      <span className={`text-[15px] font-light leading-snug ${isSelected ? 'text-brand-dark' : 'text-brand-dark/75 group-hover:text-brand-dark'}`} data-id="element-408" data-mp="dmx3petup25c">
                        {option.label}
                      </span>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${isSelected ? 'border-brand-gold bg-brand-gold text-white' : 'border-brand-dark/20'}`} data-id="element-409" data-mp="3r4677r9x6u7f">
                        {isSelected && <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-410" data-mp="1fay7stom5sga" />}
                      </span>
                    </button>;
            })}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer controls */}
      <div className="mt-6 flex items-center justify-between gap-4 border-t border-brand-dark/10 pt-6" data-id="element-411" data-mp="2fb5k58gos2yp">
        {step > 0 ? <button type="button" onClick={goBack} className="inline-flex items-center gap-2 rounded-full px-1 py-1 text-[11px] font-medium tracking-widest uppercase text-brand-dark/50 transition-colors hover:text-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-412" data-mp="37108b1st5mls">
            <ArrowLeftIcon className="h-3.5 w-3.5" strokeWidth={1.8} data-id="element-413" data-mp="2t8kcakybqoud" />
            {t('wizard.back')}
          </button> : <span data-id="element-414" data-mp="3lxrdxst2ruju" />}

        {(isMulti || stepId === 'body') && <button type="button" disabled={!canAdvance} onClick={() => goNext(isMulti ? answers.medical.join(', ') : `${answers.height} / ${answers.weight}`)} className="inline-flex items-center gap-2 rounded-full bg-brand-dark px-7 py-4 text-[11px] font-semibold tracking-[0.15em] uppercase text-brand-cream transition-all hover:bg-brand-dark/90 disabled:cursor-not-allowed disabled:opacity-35 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2" data-id="element-415" data-mp="3lvoc8bik8cza">
            {stepId === 'body' ? t('wizard.finish') : t('wizard.continue')}
            <span aria-hidden="true" data-id="element-416" data-mp="ura59mx54nxk">→</span>
          </button>}
      </div>

      {/* Reassurance stays next to the action, where it actually matters */}
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-417" data-mp="yuoosnkafmfr">
        {[t('contact.trust.secure'), t('contact.trust.response'), t('contact.trust.bilingual')].map(signal => <span key={signal} className="inline-flex items-center gap-1.5 text-[10px] font-medium tracking-widest uppercase text-brand-dark/40" data-id="element-418" data-mp="m3mez1v5oe9e">
            <CheckIcon className="h-3 w-3 text-brand-gold" strokeWidth={2.5} data-id="element-419" data-mp="zx5agf112msc" />
            {signal}
          </span>)}
      </div>
    </div>;
}
function RangeColumn({
  label,
  options,
  selected,
  onSelect
}: {
  label: string;
  options: Option[];
  selected: string;
  onSelect: (value: string) => void;
}) {
  return <div data-id="element-420" data-mp="h49uuovo5lai">
      <p className="mb-2 text-[10px] font-medium tracking-widest uppercase text-brand-dark/50" data-id="element-421" data-mp="h52tt3odz6i3">
        {label}
      </p>
      <div className="space-y-2" role="radiogroup" aria-label={label} data-id="element-422" data-mp="c4oss5l7cwtk">
        {options.map(option => {
        const isSelected = selected === option.value;
        return <button key={option.value} type="button" role="radio" aria-checked={isSelected} onClick={() => onSelect(option.value)} className={`w-full rounded-xl border px-3 py-3 text-center text-[13px] font-light transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold ${isSelected ? 'border-brand-gold bg-brand-gold/10 text-brand-dark' : 'border-brand-dark/12 bg-white text-brand-dark/75 hover:border-brand-gold/50'}`} data-id="element-423" data-mp="1nler72jdwkcm">
              {option.label}
            </button>;
      })}
      </div>
    </div>;
}
export function WizardSummary({
  answers,
  onEdit
}: {
  answers: WizardAnswers;
  onEdit: () => void;
}) {
  const {
    t
  } = useLanguage();
  const labels = t('wizard.summary.labels');
  const rows = [{
    label: labels.procedure,
    value: answers.procedure
  }, {
    label: labels.timeline,
    value: answers.timeline
  }, {
    label: labels.credit,
    value: answers.credit
  }, {
    label: labels.medical,
    value: answers.medical.join(', ')
  }, {
    label: labels.body,
    value: answers.height && answers.weight ? `${answers.height} · ${answers.weight}` : ''
  }].filter(row => !!row.value);
  return <div className="rounded-[2rem] border border-brand-gold/25 bg-brand-cream p-6 sm:p-8" data-id="element-424" data-mp="bocddhyglf9a">
      <div className="mb-5 flex items-start justify-between gap-4" data-id="element-425" data-mp="7oby5v3jd016">
        <div data-id="element-426" data-mp="2wadplw7s302">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-brand-gold/15 px-3 py-1.5" data-id="element-427" data-mp="2wk7lkkdqub1">
            <CheckIcon className="h-3 w-3 text-brand-gold" strokeWidth={3} data-id="element-428" data-mp="3u46dzkyhhavl" />
            <span className="text-[10px] font-medium tracking-widest uppercase text-brand-dark/70" data-id="element-429" data-mp="3p4jzz9atzqlq">
              {t('wizard.summary.heading')}
            </span>
          </div>
          <p className="font-serif text-xl font-light text-brand-dark leading-snug mb-1.5" data-id="element-430" data-mp="3g3c38vnyqxtr">
            {t('wizard.summary.finalStep')}
          </p>
          <p className="text-sm text-brand-dark/70 font-light leading-relaxed" data-id="element-431" data-mp="1vkjhlop7pxbp">
            {t('wizard.summary.body')}
          </p>
        </div>
        <button type="button" onClick={onEdit} className="shrink-0 rounded px-1 text-[10px] font-medium tracking-widest uppercase text-brand-dark/45 underline underline-offset-4 transition-colors hover:text-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-432" data-mp="24lzrzwg11zfx">
          {t('wizard.summary.edit')}
        </button>
      </div>

      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 border-t border-brand-dark/10 pt-5" data-id="element-433" data-mp="1gu2dtyp3uvbn">
        {rows.map(row => <div key={row.label} data-id="element-434" data-mp="1qmqcz03k5bdj">
            <dt className="text-[10px] font-medium tracking-widest uppercase text-brand-dark/40 mb-1" data-id="element-435" data-mp="1qmzzuetb32f0">
              {row.label}
            </dt>
            <dd className="text-sm text-brand-dark font-light leading-snug" data-id="element-436" data-mp="y5xfj6r7w2k8">
              {row.value}
            </dd>
          </div>)}
      </dl>

      {/* Unmistakable pointer to the form directly below */}
      <div className="mt-6 flex items-center justify-center gap-2.5 rounded-2xl bg-brand-dark px-5 py-4" data-id="element-437" data-mp="3ipfo9ymz5sqe">
        <motion.span animate={{
        y: [0, 4, 0]
      }} transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="text-brand-gold" data-id="element-438" data-mp="jjhvsgtjjf8">
          <ArrowDownIcon className="h-4 w-4" strokeWidth={2.2} data-id="element-439" data-mp="afcutcyg9exj" />
        </motion.span>
        <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-brand-cream" data-id="element-440" data-mp="5i48r1a6kqr0">
          {t('wizard.summary.arrow')}
        </span>
      </div>
    </div>;
}
export { clearWizardStorage };